import React, { useState, useEffect, useRef, useMemo } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import { X, Send, AtSign, MessageCircle, AlertTriangle } from 'lucide-react';

const ADVISORY_THREAD_QUERY = gql`
  query AdvisoryThread($atm_id: ID!) {
    advisory_thread(advisory_team_member_id: $atm_id)
  }
`;

const SEND_MESSAGE = gql`
  mutation SendMessage($cid: ID!, $body: String!) {
    send_message(conversation_id: $cid, body: $body) {
      message
      errors
    }
  }
`;

const SEND_ADVISORY_EMAIL = gql`
  mutation SendAdvisoryEmail($atm_id: ID!, $body: String!) {
    send_advisory_email(advisory_team_member_id: $atm_id, body: $body) {
      delivery_path
      errors
    }
  }
`;

function deriveInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function formatTimestamp(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch (_e) {
    return '';
  }
}

// 1:1 advisor messaging modal. Opens from the Message button on a
// filled AdvisoryTeamMember seat card. Routing decisions (in-app vs
// email vs no-email) come from the advisory_thread query's
// delivery_path field — the modal renders the right state without
// having to know about coach_profile / user accounts directly.
export default function AdvisoryMessageModal({ member, isOpen, onClose }) {
  const [body, setBody] = useState('');
  const [submitError, setSubmitError] = useState('');
  const textareaRef = useRef(null);

  const { data, loading, refetch } = useQuery(ADVISORY_THREAD_QUERY, {
    variables: { atm_id: member?.id },
    skip: !isOpen || !member?.id,
    fetchPolicy: 'cache-and-network',
  });

  const [sendMessage, { loading: sendingInApp }] = useMutation(SEND_MESSAGE);
  const [sendEmail, { loading: sendingEmail }] = useMutation(SEND_ADVISORY_EMAIL);
  const sending = sendingInApp || sendingEmail;

  useEffect(() => {
    if (isOpen) {
      setBody('');
      setSubmitError('');
      // Focus the textarea after the modal opens. setTimeout deferred
      // because the textarea isn't in the DOM until the modal mounts.
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const thread = data?.advisory_thread;
  const deliveryPath = thread?.delivery_path || 'no_email';
  const conversationId = thread?.conversation?.id;
  const messages = useMemo(() => thread?.messages || [], [thread]);
  const composeDisabled = deliveryPath === 'no_email' || sending;

  const handleSend = async () => {
    const trimmed = body.trim();
    if (!trimmed || composeDisabled) return;
    setSubmitError('');
    try {
      let errors = [];
      if (deliveryPath === 'in_app') {
        if (!conversationId) {
          setSubmitError('Conversation not ready yet. Please retry.');
          return;
        }
        const { data: result } = await sendMessage({ variables: { cid: conversationId, body: trimmed } });
        errors = result?.send_message?.errors || [];
      } else if (deliveryPath === 'email') {
        const { data: result } = await sendEmail({ variables: { atm_id: member.id, body: trimmed } });
        errors = result?.send_advisory_email?.errors || [];
      }
      if (errors.length) {
        setSubmitError(errors.join(', '));
        return;
      }
      setBody('');
      refetch();
      if (typeof window !== 'undefined' && window.ahoy?.track) {
        try {
          window.ahoy.track('succeedo_advisor_message_sent', {
            advisory_team_member_id: member.id,
            role: member.role,
            delivery_path: deliveryPath,
          });
        } catch (_e) {}
      }
    } catch (e) {
      setSubmitError(e?.message || 'Could not send. Please try again.');
    }
  };

  const handleKeyDown = (e) => {
    // Cmd/Ctrl+Enter sends the message — chat-app convention.
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen || !member) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-[hsl(var(--background))]/80 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-lg w-full max-w-[560px] max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-[hsl(var(--border))]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[hsl(var(--succeedo-orange))]/15 text-[hsl(var(--succeedo-orange))] flex items-center justify-center font-medium text-[14px]">
              {deriveInitials(member.name)}
            </div>
            <div>
              <h2 className="text-[16px] font-semibold text-[hsl(var(--foreground))]">{member.name || 'Unnamed advisor'}</h2>
              <div className="text-[12px] text-[hsl(var(--muted-foreground))]">
                {member.role_label || member.role}{member.firm ? ` · ${member.firm}` : ''}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Channel indicator */}
        <ChannelBanner deliveryPath={deliveryPath} member={member} />

        {/* Thread */}
        <div className="flex-1 overflow-y-auto p-6 space-y-3 min-h-[200px] max-h-[400px]">
          {loading && messages.length === 0 && (
            <p className="text-[13px] text-[hsl(var(--muted-foreground))] text-center">Loading…</p>
          )}
          {!loading && messages.length === 0 && deliveryPath === 'in_app' && (
            <p className="text-[13px] text-[hsl(var(--muted-foreground))] text-center py-8">
              No messages yet. Send the first one below.
            </p>
          )}
          {!loading && deliveryPath === 'email' && (
            <p className="text-[13px] text-[hsl(var(--muted-foreground))] text-center py-8">
              {member.name} isn't on Succeedo yet — your messages are sent by email.
              We don't store a thread here for off-platform advisors.
            </p>
          )}
          {messages.map((m) => (
            <div
              key={m.id}
              className={`flex ${m.sent_by_seller ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                  m.sent_by_seller
                    ? 'bg-[hsl(var(--succeedo-orange))] text-white'
                    : 'bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]'
                }`}
              >
                <p className="text-[14px] whitespace-pre-line">{m.body}</p>
                <p className={`text-[10px] mt-1 ${m.sent_by_seller ? 'text-white/70' : 'text-[hsl(var(--muted-foreground))]'}`}>
                  {formatTimestamp(m.sent_at)}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Compose */}
        <div className="border-t border-[hsl(var(--border))] p-4">
          {submitError && (
            <div className="mb-3 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-[12px] px-3 py-2">
              {submitError}
            </div>
          )}
          <div className="flex items-end gap-2">
            <textarea
              ref={textareaRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={composeDisabled}
              placeholder={deliveryPath === 'no_email'
                ? "Add an email to this advisor before messaging them"
                : "Type your message…  (⌘/Ctrl+Enter to send)"}
              rows={3}
              className="flex-1 bg-[hsl(var(--background))] border border-[hsl(var(--input))] rounded-lg px-3 py-2 text-[14px] text-[hsl(var(--foreground))] focus:border-[hsl(var(--succeedo-orange))] focus:outline-none resize-none disabled:opacity-50"
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={!body.trim() || composeDisabled}
              className="bg-[hsl(var(--succeedo-orange))] text-white rounded-full px-4 py-3 hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Send message"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ChannelBanner({ deliveryPath, member }) {
  if (deliveryPath === 'in_app') {
    return (
      <div className="px-6 py-2 bg-[hsl(var(--succeedo-orange))]/5 border-b border-[hsl(var(--border))] text-[12px] text-[hsl(var(--muted-foreground))] flex items-center gap-2">
        <MessageCircle className="w-3.5 h-3.5 text-[hsl(var(--succeedo-orange))]" />
        Real-time messaging — replies appear here
      </div>
    );
  }
  if (deliveryPath === 'email') {
    return (
      <div className="px-6 py-2 bg-amber-500/5 border-b border-[hsl(var(--border))] text-[12px] text-[hsl(var(--muted-foreground))] flex items-center gap-2">
        <AtSign className="w-3.5 h-3.5 text-amber-600" />
        Sent by email to {member.email} · replies go to your inbox
      </div>
    );
  }
  return (
    <div className="px-6 py-2 bg-destructive/5 border-b border-[hsl(var(--border))] text-[12px] text-[hsl(var(--muted-foreground))] flex items-center gap-2">
      <AlertTriangle className="w-3.5 h-3.5 text-destructive" />
      Add an email to this advisor before messaging them
    </div>
  );
}
